import { createTextCollisions } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textCollisions'
import { removeLogicFromText } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { encodeText } from '@aytm/app/stats/lib/util/helpers'

const LABEL_CLASS = 'quadrant-labeltext'
const HIDDEN_CLASS = 'hidden'

const measureText = text => {
  const span = document.createElement('span')
  span.textContent = text
  Object.assign(span.style, {
    position: 'absolute',
    top: '-100px',
    left: '0px',
    fontFamily: 'Ubuntu, Calibri, Segoe, Helvetica, Arial',
    fontSize: '12px',
    lineHeight: 'normal',
    fontWeight: 'normal'
  })
  document.body.appendChild(span)
  const rect = span.getBoundingClientRect()
  document.body.removeChild(span)
  return { w: rect.width, h: rect.height }
}

/**
 * Collects bubble marker geometry + axis lines into points[] for textCollisions.run().
 * CONTRACT with bubbleConfig: dataLabels formatter MUST return
 * <tspan class='labeltext' x='...' y='...' r='...'>text</tspan>.
 */
export const countChartLabels = (chart, container) => {
  const dataLabelsWrappers = container.querySelectorAll('.highcharts-data-labels')
  dataLabelsWrappers.forEach(el => {
    el.style.visibility = 'visible'
  })

  const points = []

  dataLabelsWrappers.forEach((wrapper, i) => {
    const labelText = wrapper.querySelector('.labeltext')
    if (!labelText) return

    const x = Number(labelText.getAttribute('x'))
    const y = Number(labelText.getAttribute('y'))
    const r = Number(labelText.getAttribute('r'))

    if (x + r < 0 || x - r > chart.chartWidth) return
    if (y + r < 0 || y - r > chart.chartHeight) return

    const text = removeLogicFromText(labelText.textContent)
    const size = measureText(text)

    points.push({
      id: i,
      circle: { x, y, r },
      label: { text, w: size.w, h: size.h }
    })
  })

  const axisLines = container.querySelectorAll('.highcharts-axis-line')
  axisLines.forEach((el, i) => {
    const d = el.getAttribute('d')
    if (!d) return
    const parts = d.split(' ')
    points.push({
      id: dataLabelsWrappers.length + i,
      line: {
        x1: Number(parts[1]),
        y1: Number(parts[2]),
        x2: Number(parts[4]),
        y2: Number(parts[5])
      }
    })
  })

  const { min, max } = chart.xAxis[0].getExtremes()
  const angleStep = (max - min) / 15

  let labels = {}
  if (Number.isFinite(angleStep)) {
    labels = createTextCollisions()
      .size(chart.chartWidth, chart.chartHeight)
      .setCirclePadding(10)
      .setAngleStep(angleStep)
      .run(points)
  }

  // Hide default labels back
  dataLabelsWrappers.forEach(el => {
    el.style.visibility = 'hidden'
  })

  return labels
}

/**
 * Removes previous custom labels, runs countChartLabels, draws
 * chart.renderer.text(...) with class `quadrant-labeltext`. For `dataLabels === 'hover'`
 * attaches DOM mouseover/out listeners directly (without d3).
 */
export const updateChartLabels = (chart, container, dataLabels = 'hover') => {
  // Remove previous
  container.querySelectorAll(`.${LABEL_CLASS}`).forEach(el => el.remove())

  const labels = countChartLabels(chart, container)

  Object.keys(labels).forEach(k => {
    const label = labels[k]
    if (!label) return

    // countChartLabels enumerates .highcharts-data-labels in DOM order, which
    // matches chart.series order, so k is a direct index. Legacy used the now
    // Highcharts-11-removed `series._i`, which would never match any series.
    const series = chart.series[Number(k)]
    if (!series || series.options.id === '-1') return

    const point = series.points[0]
    if (!point || !point.graphic) return

    // label.text was read back as textContent, so it is already decoded once and the renderer decodes again
    const text = chart.renderer.text(encodeText(label.text, false), label.x, label.y).addClass(LABEL_CLASS)

    if (dataLabels === 'hover') {
      text.addClass(HIDDEN_CLASS)

      const el = point.graphic.element
      // Property assignment instead of addEventListener — replaces previous handler
      // on repeated updateChartLabels (1:1 with d3 v3 .on() semantics).
      // addEventListener ACCUMULATES handlers -> memory leak + stale closures.
      el.onmouseover = () => {
        text.element.classList.remove(HIDDEN_CLASS)
        el.onmouseout = () => {
          text.element.classList.add(HIDDEN_CLASS)
          el.onmouseout = null
        }
      }
    }

    text.add(series.group)
  })
}
